import { FormEvent, useEffect, useRef, useState } from 'react';
import { UnauthorizedError } from '../api';

type PasswordGateProps = {
  onUnlock: (password: string) => Promise<void>;
  onComplete: () => void;
};

type Particle = {
  x: number; y: number; vx: number; vy: number; gravity: number; size: number;
  rot: number; vrot: number; color: string; emoji: string | null;
  shapeW: number; life: number; maxLife: number;
};

export default function PasswordGate({ onUnlock, onComplete }: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      if (animationRef.current) 
        cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const launchConfetti = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onComplete();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) 
      return onComplete();

    const context = canvas.getContext('2d');
    if (!context) 
      return onComplete();

    const dpr = window.devicePixelRatio || 1;
    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    context.scale(dpr, dpr);
    const colors = ['#b3812f', '#d8b86a', '#f7f3ea', '#ffffff', '#8f2f28'];
    const emojis = ['🍺', '🥨', '🍻'];
    const particles: Particle[] = Array.from({ length: 90 }, (_, index) => {
      const isEmoji = index % 6 === 0;
      return {
        x: width / 2 + (Math.random() - 0.5) * 60,
        y: height / 2 + (Math.random() - 0.5) * 40,
        vx: (Math.random() - 0.5) * 11,
        vy: -Math.random() * 11 - 4,
        gravity: 0.32,
        size: isEmoji ? 20 + Math.random() * 10 : 6 + Math.random() * 6,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        emoji: isEmoji ? emojis[Math.floor(Math.random() * emojis.length)] : null,
        shapeW: 0.6 + Math.random() * 0.5,
        life: 0,
        maxLife: 130 + Math.random() * 40,
      };
    });
    let frame = 0;
    const tick = () => {
      frame += 1;
      context.clearRect(0, 0, width, height);
      let alive = 0;
      particles.forEach((particle) => {
        if (particle.life >= particle.maxLife) 
          return;

        alive += 1;
        particle.vy += particle.gravity;
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.rot += particle.vrot;
        particle.life += 1;
        const fade = particle.life > particle.maxLife - 30
          ? Math.max(0, (particle.maxLife - particle.life) / 30)
          : 1;
        context.save();
        context.translate(particle.x, particle.y);
        context.rotate(particle.rot);
        context.globalAlpha = fade;
        if (particle.emoji) {
          context.font = `${particle.size}px sans-serif`;
          context.textAlign = 'center';
          context.textBaseline = 'middle';
          context.fillText(particle.emoji, 0, 0);
        } 
        else {
          context.fillStyle = particle.color;
          context.fillRect(
            -particle.size / 2,
            -particle.size * particle.shapeW / 2,
            particle.size,
            particle.size * particle.shapeW,
          );
        }
        context.restore();
      });
      if (alive > 0 && frame < 260) {
        animationRef.current = requestAnimationFrame(tick);
      }
      else {
        context.clearRect(0, 0, width, height);
        onComplete();
      }
    };
    animationRef.current = requestAnimationFrame(tick);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) 
      return;

    if (!password) {
      setError('Falsches Passwort — nochmal versuchen.');
      inputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onUnlock(password);
      launchConfetti();
    } 
    catch (requestError) {
      setError(requestError instanceof UnauthorizedError
        ? 'Falsches Passwort — nochmal versuchen.'
        : 'Zugang momentan nicht möglich.');
      setPassword('');
      setSubmitting(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  return (
    <div className="lock-overlay">
      <div className="diamond-background lock-diamonds" aria-hidden="true" />
      <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />
      <form className="lock-card" onSubmit={submit}>
        <div className="lock-kicker">nur für geladene Gäste</div>
        <h1>Hansi&apos;s Wiesn</h1>
        <div className="ornament ornament-dark" aria-hidden="true"><span /><i /><span /></div>
        <label className="visually-hidden" htmlFor="lock-input">Passwort</label>
        <input
          ref={inputRef}
          id="lock-input"
          type="password"
          placeholder="Passwort"
          autoComplete="current-password"
          value={password}
          disabled={submitting}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="submit" disabled={submitting}>Zugang freischalten</button>
        <div className="lock-error" role="alert">{error}</div>
      </form>
    </div>
  );
}
